import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Icon } from '@economic/gaudi';
import { useCurrentAgreement, useRouteAuthorisation, useCurrentUser, Authorise, AuthorisedLink } from '@economic/react-app';

import { CustomTab } from '../../../types';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import { Logo } from '../Logo/Logo';
import { PrimaryNavigation } from './PrimaryNavigation';
import { SecondaryNavigation } from './SecondaryNavigation';
import { useIntroductionTour } from './IntroductionTour/useIntroductionTour';
import AgreementDetails from './AgreementDetails';
import IntroductionTour from './IntroductionTour/IntroductionTour';
import Quicksearch from './Quicksearch';

import './style.css';
import { Trans } from '@lingui/macro';

export default function Header() {
    const agreement = useCurrentAgreement();
    const user = useCurrentUser();
    const agreementsQuery = useCachedQuery<CustomTab[]>('/agreementselector', []);
    const customTabsQuery = useCachedQuery<CustomTab[]>('/user/tabs/custom', []);
    const hasHome = useRouteAuthorisation('/home');
    const homeRoute = hasHome ? '/home' : '/';
    const [showTour, setShowTour] = useIntroductionTour();
    const navigate = useNavigate();

    function hideTourForeverAndNavigateToMasterInformation() {
        setShowTour(false);
        navigate('/settings/company/master-information');
    }

    return (
        <>
            {showTour && <IntroductionTour onDone={hideTourForeverAndNavigateToMasterInformation} />}

            <header id="header" className="h-16 w-full flex flex-shrink-0 items-center bg-purple">
                <NavLink id="logo" className="mx-4 h-full flex" to={homeRoute}>
                    <Logo className="h-auto my-auto align-middle border-0" white />
                </NavLink>

                <PrimaryNavigation id="primary_navigation" className="flex-grow mr-4">
                    <AuthorisedLink to="system" as={PrimaryNavigation.Link}>
                        <Icon name="sales" />
                        <Trans>System</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="home" as={PrimaryNavigation.Link} name="home">
                        <Icon name="home" />
                        <Trans>Home</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="basic" as={PrimaryNavigation.Link}>
                        <Icon name="basic" />
                        <Trans>Basic</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="developer" as={PrimaryNavigation.Link}>
                        <Icon name="developer" />
                        <Trans>Apps</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="partner-api" as={PrimaryNavigation.Link}>
                        <Icon name="partner-api" />
                        <Trans>Partner API</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="sales" as={PrimaryNavigation.Link} name="sales">
                        <Icon name="sales" />
                        <Trans>Sales</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="accounting" as={PrimaryNavigation.Link} name="accounting">
                        <Icon name="accounting" />
                        <Trans>Accounting</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="reports" as={PrimaryNavigation.Link} name="reports">
                        <Icon name="report-solid" />
                        <Trans>Reports</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="inventory" as={PrimaryNavigation.Link}>
                        <Icon name="inventory" />
                        <Trans>Inventory</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="projects" as={PrimaryNavigation.Link}>
                        <Icon name="projects" />
                        <Trans>Projects</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="website" as={PrimaryNavigation.Link}>
                        <Icon name="website" />
                        <Trans>Website</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="webshop" as={PrimaryNavigation.Link}>
                        <Icon name="webshop" />
                        <Trans>Webshop</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="webshop-plus" as={PrimaryNavigation.Link}>
                        <Icon name="webshop" />
                        <Trans>Webshop</Trans>
                    </AuthorisedLink>
                    <AuthorisedLink to="journal-pro" as={PrimaryNavigation.Link}>
                        <Icon name="journal-pro" />
                        <Trans>Journal Pro</Trans>
                    </AuthorisedLink>
                    {customTabsQuery.data.map((customTab, index) => (
                        <AuthorisedLink
                            key={index}
                            to={`custom?uri=${customTab.url}`}
                            as={PrimaryNavigation.Link}
                            className="pt-6">
                            {customTab.name}
                        </AuthorisedLink>
                    ))}
                </PrimaryNavigation>

                <Authorise path="search">
                    <Quicksearch className="ml-auto flex-grow-0 flex-shrink-0" />
                </Authorise>

                <SecondaryNavigation id="secondary_navigation" className="mr-3 ml-2" />

                <AgreementDetails
                    className="h-full"
                    currentAgreement={agreement}
                    otherAgreements={agreementsQuery.data}
                    refetchAgreements={agreementsQuery.refetch}
                    user={user}
                />
            </header>
        </>
    );
}
